import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { sendMessage } from '../chat/chatSlice';
import type { RootState } from '@/app/store';
import { calculateOrderItemPricing, type OrderItemPricingRequest } from '@/services/pricingService';
import {
  createOrder,
  fetchPendingOrderItems,
  patchOrderItems,
  updateOrder,
  type CreateOrderItemPayload,
  type OrderItemAutosavePatchPayload,
  type OrderItemAutosaveResponse,
  type PrintQualityWarning,
  type UpdateOrderPayload,
  type UpdateOrderItemPayload,
  type CreateOrderResult,
  type OrderPricingSummary,
  type PendingOrderSnapshot,
  type FulfillmentMode,
  type PickupContactPayload,
} from '@/services/orderService';
import { logging } from '@/services/logging';
import {
  computePackaging,
  type PackagingComputeResponse,
  type PackagingRequestItem,
} from '@/services/packagingService';
import {
  getShippingQuote,
  type DeliveryMode,
  type ShippingProviderCode,
  type ShippingQuoteRequestPayload,
  type ShippingQuoteResponse,
} from '@/services/shippingService';
import { getDefaultNovapostDeliveryMode } from '@/utils/novapost';
import { isOrderFlowItemStatus } from '@/types/orderStatus';
import type { OrderItemStatus } from '@/types/orderStatus';

export type OrderFulfillmentMode = FulfillmentMode;
const DEFAULT_FULFILLMENT_MODE: OrderFulfillmentMode = 'SHIPPING';

export interface ShippingAddress {
  readonly countryCode: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phone: string;
  readonly street1: string;
  readonly street2?: string;
  readonly city: string;
  readonly novaposhtaCityRef?: string;
  readonly novaposhtaWarehouseId?: string;
  readonly deliveryMode?: DeliveryMode;
  readonly shippingProvider?: string;
  readonly region: string;
  readonly postalCode: string;
  readonly setAsDefault?: boolean;
}

export interface OrderItem {
  id: string;
  orderId?: string | null;
  uploadedFileId?: string | null;
  quantity?: number;
  paperTypeId?: string;
  printerId?: string;
  width?: number;
  height?: number;
  widthIn?: number;
  heightIn?: number;
  marginIn?: number | null;
  galleryWrapIn?: number | null;
  galleryWrapMirrorEdges?: boolean | null;
  printQualityWarning?: PrintQualityWarning | null;
  colorMode?: 'COLOR' | 'BLACK_WHITE';
  status?: OrderItemStatus;
  originalWidthPx?: number;
  originalHeightPx?: number;
  originalWidthIn?: number;
  originalHeightIn?: number;
  dpiX?: number;
  dpiY?: number;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  price?: {
    value: number;
    currency: string;
  };
  pricingStatus?: 'idle' | 'loading' | 'succeeded' | 'failed';
  pricingError?: string | null;
}

const isOrderedStatus = (value: unknown): boolean => isOrderFlowItemStatus(value);

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const isPositiveFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const resolveInchesDimension = (...candidates: Array<number | null | undefined>): number | null => {
  for (const candidate of candidates) {
    if (isPositiveFiniteNumber(candidate)) {
      return candidate;
    }
  }
  return null;
};

const resolveQuantity = (value: unknown): number => {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    return Math.min(99, Math.max(1, Math.round(numeric)));
  }
  return 1;
};

export const isShippingAddressComplete = (address?: ShippingAddress): address is ShippingAddress => {
  if (!address) {
    return false;
  }
  return (
    isNonEmptyString(address.countryCode)
    && isNonEmptyString(address.firstName)
    && isNonEmptyString(address.lastName)
    && isNonEmptyString(address.phone)
    && isNonEmptyString(address.street1)
    && isNonEmptyString(address.city)
    && isNonEmptyString(address.region)
    && isNonEmptyString(address.postalCode)
  );
};

const buildShippingAddressPayload = (address?: ShippingAddress): ShippingAddress | undefined => {
  if (!isShippingAddressComplete(address)) {
    return undefined;
  }

  return {
    countryCode: address.countryCode.trim(),
    firstName: address.firstName.trim(),
    lastName: address.lastName.trim(),
    phone: address.phone.trim(),
    street1: address.street1.trim(),
    city: address.city.trim(),
    region: address.region.trim(),
    postalCode: address.postalCode.trim(),
    ...(trimOrUndefined(address.street2) ? { street2: trimOrUndefined(address.street2) } : {}),
    ...(trimOrUndefined(address.novaposhtaCityRef) ? { novaposhtaCityRef: trimOrUndefined(address.novaposhtaCityRef) } : {}),
    ...(trimOrUndefined(address.novaposhtaWarehouseId) ? { novaposhtaWarehouseId: trimOrUndefined(address.novaposhtaWarehouseId) } : {}),
    ...(address.deliveryMode ? { deliveryMode: address.deliveryMode } : {}),
    ...(trimOrUndefined(address.shippingProvider) ? { shippingProvider: trimOrUndefined(address.shippingProvider) } : {}),
    ...(typeof address.setAsDefault === 'boolean' ? { setAsDefault: address.setAsDefault } : {}),
  };
};

export const buildPickupContactPayload = (address?: ShippingAddress): PickupContactPayload | undefined => {
  if (!address) {
    return undefined;
  }

  const countryCode = address.countryCode.trim();
  const firstName = address.firstName.trim();
  const phone = address.phone.trim();
  const lastName = trimOrUndefined(address.lastName);

  if (!countryCode || !firstName || !phone) {
    return undefined;
  }

  return {
    countryCode,
    firstName,
    phone,
    ...(lastName ? { lastName } : {}),
    ...(typeof address.setAsDefault === 'boolean' ? { setAsDefault: address.setAsDefault } : {}),
  };
};

export const buildCheckoutContactPayload = (
  address: ShippingAddress | undefined,
  fulfillmentMode: OrderFulfillmentMode,
): ShippingAddress | PickupContactPayload | undefined => (
  fulfillmentMode === 'PICKUP'
    ? buildPickupContactPayload(address)
    : buildShippingAddressPayload(address)
);

const trimOrUndefined = (value: string | undefined | null): string | undefined => {
  if (!isNonEmptyString(value)) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const extractFulfillmentMode = (paymentInformation: unknown): OrderFulfillmentMode => {
  if (!paymentInformation || typeof paymentInformation !== 'object' || Array.isArray(paymentInformation)) {
    return DEFAULT_FULFILLMENT_MODE;
  }

  const shipping = (paymentInformation as { shipping?: unknown }).shipping;
  if (!shipping || typeof shipping !== 'object' || Array.isArray(shipping)) {
    return DEFAULT_FULFILLMENT_MODE;
  }

  return (shipping as { fulfillmentMode?: unknown }).fulfillmentMode === 'PICKUP'
    ? 'PICKUP'
    : DEFAULT_FULFILLMENT_MODE;
};

const applyFulfillmentModeToPaymentInformation = (
  paymentInformation: OrderState['paymentInformation'],
  fulfillmentMode: OrderFulfillmentMode,
): OrderState['paymentInformation'] => {
  const base = paymentInformation && typeof paymentInformation === 'object' && !Array.isArray(paymentInformation)
    ? { ...(paymentInformation as Record<string, unknown>) }
    : {};
  const shipping = base.shipping && typeof base.shipping === 'object' && !Array.isArray(base.shipping)
    ? { ...(base.shipping as Record<string, unknown>) }
    : {};

  shipping.fulfillmentMode = fulfillmentMode;
  base.shipping = shipping;
  return base;
};

const normalizeString = (value: string | undefined | null): string => (typeof value === 'string' ? value.trim() : '');

const hasShippingAddressChanged = (
  previous: ShippingAddress | undefined,
  next: ShippingAddress | undefined,
): boolean => {
  if (!next) {
    return Boolean(previous);
  }
  if (!previous) {
    return true;
  }

  return (
    normalizeString(previous.countryCode) !== normalizeString(next.countryCode)
    || normalizeString(previous.firstName) !== normalizeString(next.firstName)
    || normalizeString(previous.lastName) !== normalizeString(next.lastName)
    || normalizeString(previous.phone) !== normalizeString(next.phone)
    || normalizeString(previous.street1) !== normalizeString(next.street1)
    || normalizeString(previous.street2) !== normalizeString(next.street2)
    || normalizeString(previous.city) !== normalizeString(next.city)
    || normalizeString(previous.novaposhtaCityRef) !== normalizeString(next.novaposhtaCityRef)
    || normalizeString(previous.region) !== normalizeString(next.region)
    || normalizeString(previous.postalCode) !== normalizeString(next.postalCode)
    || Boolean(previous.setAsDefault) !== Boolean(next.setAsDefault)
  );
};

const buildPackagingRequestItems = (items: ReadonlyArray<OrderItem>): PackagingRequestItem[] => {
  const results: PackagingRequestItem[] = [];

  for (const item of items) {
    if (!item?.id) {
      continue;
    }

    const widthIn = resolveInchesDimension(item.widthIn ?? null, item.originalWidthIn ?? null);
    const heightIn = resolveInchesDimension(item.heightIn ?? null, item.originalHeightIn ?? null);

    if (!isPositiveFiniteNumber(widthIn) || !isPositiveFiniteNumber(heightIn)) {
      continue;
    }

    const rawGsm = (item as { gsm?: unknown }).gsm;
    const gsm = isPositiveFiniteNumber(rawGsm) ? rawGsm : undefined;
    const galleryWrapIn = isPositiveFiniteNumber(item.galleryWrapIn) ? item.galleryWrapIn : undefined;
    const paperTypeId = isNonEmptyString(item.paperTypeId) ? item.paperTypeId.trim() : undefined;
    const rawQuantity = (item as { quantity?: unknown }).quantity ?? (item as { copies?: unknown }).copies;
    const quantity = resolveQuantity(rawQuantity);

    results.push({
      widthIn,
      heightIn,
      galleryWrapIn,
      quantity,
      gsm,
      paperTypeId,
    });
  }

  return results;
};

const SHIPPING_QUOTE_PROVIDERS: ReadonlyArray<ShippingProviderCode> = ['NOVAPOST', 'UKRPOSHTA'];

export const ORDER_ITEM_AUTOSAVE_FIELDS = ['quantity', 'paperTypeId', 'printerId', 'widthIn', 'heightIn', 'marginIn', 'galleryWrapIn', 'galleryWrapMirrorEdges', 'printQualityWarning', 'colorMode'] as const;
export type OrderItemDirtyField = (typeof ORDER_ITEM_AUTOSAVE_FIELDS)[number];

type DirtyFieldEntry = Partial<Record<OrderItemDirtyField, true>>;
type SavedFieldSnapshot = {
  quantity?: number;
  paperTypeId?: string;
  printerId?: string;
  widthIn?: number;
  heightIn?: number;
  marginIn?: number | null;
  galleryWrapIn?: number | null;
  galleryWrapMirrorEdges?: boolean | null;
  printQualityWarning?: PrintQualityWarning | null;
  colorMode?: OrderItem['colorMode'];
};

const MAX_SELECTION_SIZE = 100;

const clampSelection = (selected: readonly string[]): string[] => {
  return Array.from(new Set(selected)).slice(0, MAX_SELECTION_SIZE);
};

export const hasPersistedOrderId = (id: unknown): id is string => typeof id === 'string' && id.trim().length > 0;

const deriveSelectableIds = (items: OrderItem[]): readonly string[] => {
  return items
    .map((item) => item.id)
    .filter((id): id is string => Boolean(id));
};

const normalizeSelection = (items: OrderItem[], selected: readonly string[] | undefined): string[] => {
  const allowedIds = new Set(deriveSelectableIds(items));
  const nextSelection = new Set<string>();

  if (Array.isArray(selected)) {
    for (const id of selected) {
      if (allowedIds.has(id)) {
        nextSelection.add(id);
      }
    }
  }

  return clampSelection(Array.from(nextSelection));
};

interface OrderState {
  id?: string | null; // Unique identifier from backend
  status?: string; // e.g., 'NEW', 'CONFIGURED' to match OrderStatus enum
  shippingAddress?: ShippingAddress;
  fulfillmentMode: OrderFulfillmentMode;
  paymentInformation?: {
    method?: string; // e.g., 'credit_card', 'paypal'
    status?: string; // e.g., 'pending', 'completed'
    shipping?: {
      provider?: string | null;
      deliveryMode?: DeliveryMode | null;
      novaposhtaCityRef?: string | null;
      novaposhtaWarehouseId?: string | null;
      fulfillmentMode?: OrderFulfillmentMode;
    };
  };
  items: OrderItem[];
  selectedItemIds: string[];
  dirtyFieldsByItemId: Record<string, DirtyFieldEntry>;
  lastSavedFieldValuesByItemId: Record<string, SavedFieldSnapshot>;
  pricingSummary?: {
    currency: string;
    itemsSubtotal: number;
    total: number;
    pricedItemCount: number;
    itemCount: number;
    hasUnpricedItems: boolean;
  };
  shippingQuote?: ShippingQuoteResponse;
  shippingQuoteStatus?: 'idle' | 'loading' | 'succeeded' | 'failed';
  shippingQuoteError?: string | null;
  packagingSummary?: PackagingComputeResponse;
  checkoutPersistenceInFlight: boolean;
  pricingCacheVersion?: number;
}

// Ensure incoming payloads are item-centric.
// We no longer read or migrate any root-level fields (uploadedFileId, paperTypeId, printerId, widthIn, heightIn).
// Producers (AI/UI) must send item data under state.items[].
const normalizeToItemCentric = (state: OrderState): OrderState => {
  const items: OrderItem[] = Array.isArray(state.items) ? state.items : [];
  const selectedItemIds = normalizeSelection(items, state.selectedItemIds);
  const normalized: OrderState = {
    ...state,
    items,
    selectedItemIds,
    dirtyFieldsByItemId: state.dirtyFieldsByItemId ?? {},
    lastSavedFieldValuesByItemId: state.lastSavedFieldValuesByItemId ?? {},
  };
  const validIds = new Set(items.map(item => item.id));
  pruneTrackingMaps(normalized, validIds);
  return normalized;
}

const initialState: OrderState = {
  items: [],
  selectedItemIds: [],
  dirtyFieldsByItemId: {},
  lastSavedFieldValuesByItemId: {},
  pricingSummary: undefined,
  fulfillmentMode: DEFAULT_FULFILLMENT_MODE,
  shippingQuote: undefined,
  shippingQuoteStatus: 'idle',
  shippingQuoteError: null,
  packagingSummary: undefined,
  checkoutPersistenceInFlight: false,
  pricingCacheVersion: 0,
};

const buildOrderStateFromPendingSnapshot = (
  snapshot: PendingOrderSnapshot,
  currentState: OrderState,
): OrderState => {
  const pendingOrder = snapshot.order;
  const items: OrderItem[] = (pendingOrder.items ?? []).map((item) => ({
    id: item.id,
    orderId: item.orderId ?? null,
    uploadedFileId: item.uploadedFileId ?? null,
    quantity: resolveQuantity(item.quantity),
    paperTypeId: item.paperTypeId ?? undefined,
    printerId: item.printerId ?? undefined,
    widthIn: item.widthIn ?? undefined,
    heightIn: item.heightIn ?? undefined,
    marginIn: item.marginIn ?? undefined,
    galleryWrapIn: item.galleryWrapIn ?? undefined,
    galleryWrapMirrorEdges: item.galleryWrapMirrorEdges ?? undefined,
    printQualityWarning: item.printQualityWarning ?? undefined,
    colorMode: item.colorMode ?? undefined,
    status: item.status,
    originalWidthPx: item.originalWidthPx ?? undefined,
    originalHeightPx: item.originalHeightPx ?? undefined,
    originalWidthIn: item.originalWidthIn ?? undefined,
    originalHeightIn: item.originalHeightIn ?? undefined,
    dpiX: item.dpiX ?? undefined,
    dpiY: item.dpiY ?? undefined,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    price: typeof item.priceUsd === 'number' && Number.isFinite(item.priceUsd)
      ? { value: item.priceUsd, currency: 'USD' }
      : undefined,
    pricingStatus: typeof item.priceUsd === 'number' && Number.isFinite(item.priceUsd) ? 'succeeded' : 'idle',
    pricingError: null,
  }));

  const nextState: OrderState = {
    ...initialState,
    id: pendingOrder.id ?? undefined,
    status: pendingOrder.status ?? undefined,
    items,
    selectedItemIds: currentState.selectedItemIds.filter((itemId) => items.some((item) => item.id === itemId)),
    shippingAddress: currentState.shippingAddress,
    fulfillmentMode: extractFulfillmentMode(pendingOrder.paymentInformation),
    paymentInformation: pendingOrder.paymentInformation as OrderState['paymentInformation'],
    dirtyFieldsByItemId: {},
    lastSavedFieldValuesByItemId: {},
    shippingQuote: undefined,
    shippingQuoteStatus: 'idle',
    shippingQuoteError: null,
    packagingSummary: undefined,
    checkoutPersistenceInFlight: false,
  };

  items.forEach((item) => {
    nextState.lastSavedFieldValuesByItemId[item.id] = pickTrackedFieldValues(item);
  });

  return nextState;
};

const findItemById = (state: OrderState, itemId: string): OrderItem | undefined => {
  return state.items.find(candidate => candidate.id === itemId);
};

const getOrderedItems = (state: OrderState): OrderItem[] => {
  return state.items.filter((item) => {
    const status = typeof item?.status === 'string' ? item.status.toUpperCase() : '';
    return isOrderedStatus(status);
  });
};

const resetShippingQuoteState = (state: OrderState): void => {
  state.shippingQuoteStatus = 'idle';
  state.shippingQuote = undefined;
  state.shippingQuoteError = null;
  state.packagingSummary = undefined;
};

const areOptionalNumbersEqual = (left: number | null | undefined, right: number | null | undefined): boolean => {
  const leftNormalized = typeof left === 'number' && Number.isFinite(left) ? left : undefined;
  const rightNormalized = typeof right === 'number' && Number.isFinite(right) ? right : undefined;
  return leftNormalized === rightNormalized;
};

const normalizePrintQualityWarning = (value: unknown): PrintQualityWarning | null | undefined => {
  if (value == null) {
    return undefined;
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }
  const candidate = value as Partial<PrintQualityWarning>;
  if (candidate.type !== 'LOW_PPI') {
    return undefined;
  }
  if (typeof candidate.ppi !== 'number' || !Number.isFinite(candidate.ppi) || candidate.ppi <= 0) {
    return undefined;
  }
  if (typeof candidate.threshold !== 'number' || !Number.isFinite(candidate.threshold) || candidate.threshold <= 0) {
    return undefined;
  }
  return {
    type: 'LOW_PPI',
    ppi: candidate.ppi,
    threshold: candidate.threshold,
    ...(typeof candidate.acknowledged === 'boolean' ? { acknowledged: candidate.acknowledged } : {}),
    ...(typeof candidate.reason === 'string' && candidate.reason.trim().length > 0
      ? { reason: candidate.reason.trim() }
      : {}),
  };
};

const arePrintQualityWarningsEqual = (left: unknown, right: unknown): boolean => {
  const normalizedLeft = normalizePrintQualityWarning(left) ?? null;
  const normalizedRight = normalizePrintQualityWarning(right) ?? null;
  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
};

const hasPackagingRelevantOrderItemChanges = (previous: OrderItem, next: OrderItem): boolean => {
  if (!areOptionalNumbersEqual(previous.widthIn, next.widthIn)) {
    return true;
  }
  if (!areOptionalNumbersEqual(previous.heightIn, next.heightIn)) {
    return true;
  }

  const previousPaperTypeId = typeof previous.paperTypeId === 'string' ? previous.paperTypeId.trim() : '';
  const nextPaperTypeId = typeof next.paperTypeId === 'string' ? next.paperTypeId.trim() : '';
  if (previousPaperTypeId !== nextPaperTypeId) {
    return true;
  }

  const previousRecord = previous as OrderItem & { quantity?: number; copies?: number };
  const nextRecord = next as OrderItem & { quantity?: number; copies?: number };
  const previousQuantity = typeof previousRecord.quantity === 'number'
    ? previousRecord.quantity
    : (typeof previousRecord.copies === 'number' ? previousRecord.copies : undefined);
  const nextQuantity = typeof nextRecord.quantity === 'number'
    ? nextRecord.quantity
    : (typeof nextRecord.copies === 'number' ? nextRecord.copies : undefined);

  return !areOptionalNumbersEqual(previousQuantity, nextQuantity);
};

const ensureTrackingMaps = (state: OrderState): void => {
  if (!state.dirtyFieldsByItemId) {
    state.dirtyFieldsByItemId = {};
  }
  if (!state.lastSavedFieldValuesByItemId) {
    state.lastSavedFieldValuesByItemId = {};
  }
};

const mergePricingSummary = (
  state: OrderState,
  summary?: OrderPricingSummary,
) => {
  if (!summary) {
    state.pricingSummary = undefined;
    return;
  }
  state.pricingSummary = {
    currency: summary.currency,
    itemsSubtotal: summary.itemsSubtotal,
    total: summary.total,
    pricedItemCount: summary.pricedItemCount,
    itemCount: summary.itemCount,
    hasUnpricedItems: summary.hasUnpricedItems,
  };
};

const hydrateOrderStateFromResult = (state: OrderState, result: CreateOrderResult): void => {
  state.id = hasPersistedOrderId(result.id) ? result.id : undefined;
  if (typeof result.status === 'string') {
    state.status = result.status;
  }
  if (Array.isArray(result.orderItems)) {
    const byId = new Map(state.items.map((item) => [item.id, item] as const));

    for (const entry of result.orderItems) {
      const existing = byId.get(entry.id);
      const next: OrderItem = existing ? { ...existing } : { id: entry.id };

      next.orderId = entry.orderId ?? next.orderId ?? state.id;
      if (entry.uploadedFileId !== undefined) {
        next.uploadedFileId = entry.uploadedFileId ?? undefined;
      }
      if (entry.quantity !== undefined) {
        next.quantity = entry.quantity == null ? undefined : resolveQuantity(entry.quantity);
      }
      if (entry.paperTypeId !== undefined) {
        next.paperTypeId = entry.paperTypeId ?? undefined;
      }
      if (entry.printerId !== undefined) {
        next.printerId = entry.printerId ?? undefined;
      }
      if (entry.widthIn !== undefined) {
        next.widthIn = entry.widthIn ?? undefined;
      }
      if (entry.heightIn !== undefined) {
        next.heightIn = entry.heightIn ?? undefined;
      }
      if (entry.marginIn !== undefined) {
        next.marginIn = entry.marginIn ?? undefined;
      }
      if (entry.galleryWrapIn !== undefined) {
        next.galleryWrapIn = entry.galleryWrapIn ?? undefined;
      }
      if (entry.galleryWrapMirrorEdges !== undefined) {
        next.galleryWrapMirrorEdges = entry.galleryWrapMirrorEdges ?? undefined;
      }
      if (entry.printQualityWarning !== undefined) {
        next.printQualityWarning = normalizePrintQualityWarning(entry.printQualityWarning) ?? null;
      }
      if (entry.colorMode !== undefined) {
        next.colorMode = entry.colorMode ?? undefined;
      }
      if (typeof entry.status === 'string') {
        next.status = entry.status;
      }
      if (typeof entry.priceUsd === 'number') {
        next.price = { value: entry.priceUsd, currency: 'USD' };
      } else if (entry.priceUsd == null) {
        next.price = undefined;
      }
      next.updatedAt = entry.updatedAt ?? next.updatedAt;

      byId.set(entry.id, next);
    }

    state.items = Array.from(byId.values());
  }
  mergePricingSummary(state, result.pricingSummary);
  if (result.paymentInformation !== undefined) {
    state.paymentInformation = result.paymentInformation as OrderState['paymentInformation'];
    state.fulfillmentMode = extractFulfillmentMode(result.paymentInformation);
  }
  state.selectedItemIds = normalizeSelection(state.items, state.selectedItemIds);
  resetDirtyStateUsingCurrentItems(state);
};

const removeItemsById = (state: OrderState, ids: Iterable<string>): void => {
  const removalSet = new Set<string>();
  for (const id of ids) {
    if (typeof id === 'string' && id.length > 0) {
      removalSet.add(id);
    }
  }

  if (removalSet.size === 0) {
    return;
  }

  ensureTrackingMaps(state);

  state.items = state.items.filter((item) => {
    if (!item?.id || !removalSet.has(item.id)) {
      return true;
    }

    delete state.dirtyFieldsByItemId[item.id];
    delete state.lastSavedFieldValuesByItemId[item.id];
    return false;
  });

  state.selectedItemIds = normalizeSelection(state.items, state.selectedItemIds);
};

const normalizeSavedFieldValue = <K extends OrderItemDirtyField>(field: K, value: OrderItem[K] | undefined) => {
  if (value == null) {
    return undefined;
  }
  switch (field) {
    case 'paperTypeId':
    case 'printerId':
      return typeof value === 'string' && value.length > 0 ? value : undefined;
    case 'quantity':
      return typeof value === 'number' && Number.isFinite(value) ? resolveQuantity(value) : undefined;
    case 'colorMode':
      return value === 'COLOR' || value === 'BLACK_WHITE' ? value : undefined;
    case 'widthIn':
    case 'heightIn':
      return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
    case 'marginIn':
    case 'galleryWrapIn':
      if (value === null) {
        return null;
      }
      return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
    case 'galleryWrapMirrorEdges':
      if (value === null) {
        return null;
      }
      return typeof value === 'boolean' ? value : undefined;
    case 'printQualityWarning':
      return normalizePrintQualityWarning(value);
    default:
      return undefined;
  }
};

const setSavedFieldValue = <K extends OrderItemDirtyField>(
  snapshot: SavedFieldSnapshot,
  field: K,
  value: OrderItem[K] | undefined,
) => {
  const normalized = normalizeSavedFieldValue(field, value);
  switch (field) {
    case 'quantity': {
      if (normalized === undefined) {
        delete snapshot.quantity;
      } else {
        snapshot.quantity = normalized as number;
      }
      break;
    }
    case 'paperTypeId': {
      if (normalized === undefined) {
        delete snapshot.paperTypeId;
      } else {
        snapshot.paperTypeId = normalized as string;
      }
      break;
    }
    case 'printerId': {
      if (normalized === undefined) {
        delete snapshot.printerId;
      } else {
        snapshot.printerId = normalized as string;
      }
      break;
    }
    case 'widthIn': {
      if (normalized === undefined) {
        delete snapshot.widthIn;
      } else {
        snapshot.widthIn = normalized as number;
      }
      break;
    }
    case 'heightIn': {
      if (normalized === undefined) {
        delete snapshot.heightIn;
      } else {
        snapshot.heightIn = normalized as number;
      }
      break;
    }
    case 'colorMode': {
      if (normalized === undefined) {
        delete snapshot.colorMode;
      } else {
        snapshot.colorMode = normalized as OrderItem['colorMode'];
      }
      break;
    }
    case 'marginIn': {
      if (normalized === undefined) {
        delete snapshot.marginIn;
      } else {
        snapshot.marginIn = normalized as number | null;
      }
      break;
    }
    case 'galleryWrapIn': {
      if (normalized === undefined) {
        delete snapshot.galleryWrapIn;
      } else {
        snapshot.galleryWrapIn = normalized as number | null;
      }
      break;
    }
    case 'galleryWrapMirrorEdges': {
      if (normalized === undefined) {
        delete snapshot.galleryWrapMirrorEdges;
      } else {
        snapshot.galleryWrapMirrorEdges = normalized as boolean | null;
      }
      break;
    }
    case 'printQualityWarning': {
      if (normalized === undefined) {
        delete snapshot.printQualityWarning;
      } else {
        snapshot.printQualityWarning = normalized as PrintQualityWarning | null;
      }
      break;
    }
  }
};

const pickTrackedFieldValues = (source?: Partial<OrderItem>): SavedFieldSnapshot => {
  const snapshot: SavedFieldSnapshot = {};
  if (!source) {
    return snapshot;
  }
  for (const field of ORDER_ITEM_AUTOSAVE_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(source, field)) {
      setSavedFieldValue(snapshot, field, source[field]);
    }
  }
  return snapshot;
};

const syncDirtyStateForItem = (state: OrderState, orderItemId: string): void => {
  const item = findItemById(state, orderItemId);
  if (!item) {
    return;
  }
  const resolvedId = item.id;
  const snapshot = state.lastSavedFieldValuesByItemId[resolvedId] ?? {};
  const dirtyEntry: DirtyFieldEntry = {};
  let hasDirty = false;

  for (const field of ORDER_ITEM_AUTOSAVE_FIELDS) {
    const currentValue = item[field];
    const savedValue = snapshot[field];
    const isEqual = field === 'printQualityWarning'
      ? arePrintQualityWarningsEqual(currentValue, savedValue)
      : Object.is(currentValue, savedValue);
    if (!isEqual) {
      dirtyEntry[field] = true;
      hasDirty = true;
    }
  }

  if (hasDirty) {
    state.dirtyFieldsByItemId[resolvedId] = dirtyEntry;
  } else {
    delete state.dirtyFieldsByItemId[resolvedId];
  }
};

const pruneTrackingMaps = (state: OrderState, validIds: Set<string>): void => {
  for (const key of Object.keys(state.dirtyFieldsByItemId)) {
    if (!validIds.has(key)) {
      delete state.dirtyFieldsByItemId[key];
    }
  }
  for (const key of Object.keys(state.lastSavedFieldValuesByItemId)) {
    if (!validIds.has(key)) {
      delete state.lastSavedFieldValuesByItemId[key];
    }
  }
};

const buildPatchFromItem = (
  item: OrderItem,
  fields: readonly OrderItemDirtyField[],
): OrderItemAutosavePatchPayload => {
  const patch: OrderItemAutosavePatchPayload = {
    orderItemId: item.id,
  };

  for (const field of fields) {
    switch (field) {
      case 'quantity': {
        patch.quantity = resolveQuantity(item.quantity);
        break;
      }
      case 'paperTypeId': {
        const value = item.paperTypeId;
        patch.paperTypeId = typeof value === 'string' && value.length > 0 ? value : null;
        break;
      }
      case 'printerId': {
        const value = item.printerId;
        patch.printerId = typeof value === 'string' && value.length > 0 ? value : null;
        break;
      }
      case 'colorMode': {
        const value = item.colorMode;
        patch.colorMode = value === 'COLOR' || value === 'BLACK_WHITE' ? value : null;
        break;
      }
      case 'widthIn': {
        const value = item.widthIn;
        patch.widthIn = typeof value === 'number' && Number.isFinite(value) ? value : null;
        break;
      }
      case 'heightIn': {
        const value = item.heightIn;
        patch.heightIn = typeof value === 'number' && Number.isFinite(value) ? value : null;
        break;
      }
      case 'marginIn': {
        const value = item.marginIn;
        patch.marginIn = typeof value === 'number' && Number.isFinite(value) ? value : null;
        break;
      }
      case 'galleryWrapIn': {
        const value = item.galleryWrapIn;
        patch.galleryWrapIn = typeof value === 'number' && Number.isFinite(value) ? value : null;
        break;
      }
      case 'galleryWrapMirrorEdges': {
        const value = item.galleryWrapMirrorEdges;
        patch.galleryWrapMirrorEdges = typeof value === 'boolean' ? value : null;
        break;
      }
      case 'printQualityWarning': {
        patch.printQualityWarning = normalizePrintQualityWarning(item.printQualityWarning) ?? null;
        break;
      }
    }
  }

  const normalizedWidth = typeof item.widthIn === 'number' && Number.isFinite(item.widthIn) ? item.widthIn : undefined;
  if (normalizedWidth !== undefined) {
    patch.widthIn = normalizedWidth;
  }

  const normalizedHeight = typeof item.heightIn === 'number' && Number.isFinite(item.heightIn) ? item.heightIn : undefined;
  if (normalizedHeight !== undefined) {
    patch.heightIn = normalizedHeight;
  }

  const normalizedMargin = typeof item.marginIn === 'number' && Number.isFinite(item.marginIn) ? item.marginIn : undefined;
  if (normalizedMargin !== undefined) {
    patch.marginIn = normalizedMargin;
  }

  const normalizedGalleryWrap = typeof item.galleryWrapIn === 'number' && Number.isFinite(item.galleryWrapIn) ? item.galleryWrapIn : undefined;
  if (normalizedGalleryWrap !== undefined) {
    patch.galleryWrapIn = normalizedGalleryWrap;
  }

  if (typeof item.galleryWrapMirrorEdges === 'boolean') {
    patch.galleryWrapMirrorEdges = item.galleryWrapMirrorEdges;
  }

  if (item.printQualityWarning !== undefined) {
    patch.printQualityWarning = normalizePrintQualityWarning(item.printQualityWarning) ?? null;
  }

  return patch;
};

const resetDirtyStateUsingCurrentItems = (state: OrderState): void => {
  const validIds = new Set<string>();
  for (const item of state.items) {
    validIds.add(item.id);
    state.lastSavedFieldValuesByItemId[item.id] = pickTrackedFieldValues(item);
    delete state.dirtyFieldsByItemId[item.id];
  }
  pruneTrackingMaps(state, validIds);
};

export const recalculateOrderItemPrice = createAsyncThunk<
  {
    orderItemId: string;
    uploadedFileId: string;
    price: { value: number; currency: string };
    resolvedPaperTypeId: string;
    resolvedPrinterId: string;
    dimensions: { widthInches: number; heightInches: number };
  },
  { orderItemId: string },
  { state: RootState }
>(
  'order/recalculateOrderItemPrice',
  async ({ orderItemId }, { getState, rejectWithValue, signal }) => {
    const state = getState();
    const item = state.order.items.find((candidate) => candidate.id === orderItemId);

    if (!item) {
      return rejectWithValue(`Order item ${orderItemId} not found`);
    }

    const targetFileId = item.uploadedFileId;
    if (!targetFileId) {
      return rejectWithValue(`Order item ${orderItemId} is missing uploadedFileId`);
    }

    const payload: OrderItemPricingRequest = {
      uploadedFileId: targetFileId,
    };

    if (typeof item.widthIn === 'number' && Number.isFinite(item.widthIn)) {
      payload.widthInches = item.widthIn;
    }
    if (typeof item.heightIn === 'number' && Number.isFinite(item.heightIn)) {
      payload.heightInches = item.heightIn;
    }
    if (typeof item.galleryWrapIn === 'number' && Number.isFinite(item.galleryWrapIn) && item.galleryWrapIn > 0) {
      payload.galleryWrapInches = item.galleryWrapIn;
    }
    if (typeof item.paperTypeId === 'string' && item.paperTypeId.trim().length > 0) {
      payload.paperTypeId = item.paperTypeId;
    }
    if (typeof item.printerId === 'string' && item.printerId.trim().length > 0) {
      payload.printerId = item.printerId;
    }

    const result = await calculateOrderItemPricing(payload, { signal });

    return {
      orderItemId: item.id,
      uploadedFileId: targetFileId,
      price: result.price,
      resolvedPaperTypeId: result.resolvedPaperTypeId,
      resolvedPrinterId: result.resolvedPrinterId,
      dimensions: result.dimensions,
    };
  }
);

export const fetchShippingQuotes = createAsyncThunk<
  { quote: ShippingQuoteResponse; packaging: PackagingComputeResponse },
  { deliveryMode?: DeliveryMode; officeId?: string } | void,
  { state: RootState; rejectValue: string }
>(
  'order/fetchShippingQuotes',
  async (params, { getState, rejectWithValue }) => {
    const state = getState();
    const orderState = state.order;
    const orderId = orderState.id;

    if (!hasPersistedOrderId(orderId)) {
      return rejectWithValue('order.shipping.missingOrderId');
    }

    if (orderState.fulfillmentMode === 'PICKUP') {
      return rejectWithValue('order.shipping.pickupMode');
    }

    if (!isShippingAddressComplete(orderState.shippingAddress)) {
      return rejectWithValue('order.shipping.addressIncomplete');
    }

    const orderedItems = getOrderedItems(orderState);
    const packagingItems = buildPackagingRequestItems(orderedItems);

    if (packagingItems.length === 0) {
      return rejectWithValue('order.shipping.noEligibleItems');
    }

    let packaging: PackagingComputeResponse;

    try {
      packaging = await computePackaging({ items: packagingItems });
    } catch (error) {
      logging.error('[order] Failed to compute packaging for shipping quote', { orderId, error });
      const message = error instanceof Error ? error.message : 'order.shipping.packagingFailed';
      return rejectWithValue(message);
    }

    const { totalWeightGrams, packageDimensions } = packaging;

    if (!isPositiveFiniteNumber(totalWeightGrams)) {
      return rejectWithValue('order.shipping.invalidWeight');
    }

    if (
      !packageDimensions
      || !isPositiveFiniteNumber(packageDimensions.lengthIn)
      || !isPositiveFiniteNumber(packageDimensions.widthIn)
      || !isPositiveFiniteNumber(packageDimensions.heightIn)
    ) {
      return rejectWithValue('order.shipping.invalidDimensions');
    }

    const destination: ShippingQuoteRequestPayload['destination'] = {
      countryCode: orderState.shippingAddress.countryCode.trim(),
      region: orderState.shippingAddress.region.trim(),
      city: orderState.shippingAddress.city.trim(),
      cityId: trimOrUndefined(orderState.shippingAddress.novaposhtaCityRef),
      postcode: trimOrUndefined(orderState.shippingAddress.postalCode),
      street: trimOrUndefined(orderState.shippingAddress.street1),
      house: trimOrUndefined(orderState.shippingAddress.street2),
      apartment: undefined,
    };

    const novapostDelivery = {
      mode: params?.deliveryMode
        ?? orderState.shippingAddress.deliveryMode
        ?? getDefaultNovapostDeliveryMode(orderState.shippingAddress.countryCode),
      officeId: params?.officeId ?? orderState.shippingAddress.novaposhtaWarehouseId,
    } satisfies ShippingQuoteRequestPayload['delivery'];

    const buildProviderPayload = (provider: ShippingProviderCode): ShippingQuoteRequestPayload => ({
      orderId: orderId.trim(),
      destination,
      package: {
        weightGrams: totalWeightGrams,
        lengthIn: packageDimensions.lengthIn,
        widthIn: packageDimensions.widthIn,
        heightIn: packageDimensions.heightIn,
      },
      delivery: {
        mode: provider === 'NOVAPOST' ? novapostDelivery.mode : 'BRANCH',
        officeId: provider === 'NOVAPOST' ? novapostDelivery.officeId : undefined,
      },
      quoteOptions: provider === 'UKRPOSHTA'
        ? {
            excludePackageTypes: ['LETTER'],
          }
        : undefined,
    });

    const providerResults = await Promise.allSettled(
      SHIPPING_QUOTE_PROVIDERS.map((provider) => getShippingQuote(provider, buildProviderPayload(provider))),
    );

    const successfulQuotes: ShippingQuoteResponse[] = [];
    const failedReasons: string[] = [];

    for (const result of providerResults) {
      if (result.status === 'fulfilled') {
        successfulQuotes.push(result.value);
      } else {
        const reason = result.reason instanceof Error ? result.reason.message : 'order.shipping.quoteFailed';
        failedReasons.push(reason);
      }
    }

    if (successfulQuotes.length === 0) {
      const message = failedReasons[0] ?? 'order.shipping.quoteFailed';
      logging.error('[order] Failed to fetch shipping quote for all providers', { orderId, errors: failedReasons });
      return rejectWithValue(message);
    }

    const mergedOptions = successfulQuotes.flatMap((quote) =>
      quote.options.map((option) => ({
        ...option,
        provider: quote.provider,
      })),
    );

    if (mergedOptions.length === 0) {
      logging.error('[order] Shipping providers returned no options', {
        orderId,
        providers: successfulQuotes.map((quote) => quote.provider),
      });
      return rejectWithValue('order.shipping.quoteFailed');
    }

    const mergedQuote: ShippingQuoteResponse = {
      provider: successfulQuotes[0].provider,
      options: mergedOptions,
      cache: undefined,
    };

    return { quote: mergedQuote, packaging };
  },
);

export const fetchUkrposhtaShippingQuote = fetchShippingQuotes;

export const createOrderForCheckout = createAsyncThunk<
  {
    orderId: string;
    orderItems: ReadonlyArray<{
      id: string;
      uploadedFileId?: string | null;
    }>;
    consumedItemIds?: ReadonlyArray<string>;
    raw: CreateOrderResult;
  },
  void,
  { state: RootState }
>(
  'order/createOrderForCheckout',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const orderState = state.order;
    const serviceId = state.service.currentService?.id;

    if (hasPersistedOrderId(orderState.id)) {
      return rejectWithValue('order.create.alreadyHasOrderId');
    }

    if (!serviceId) {
      return rejectWithValue('order.create.missingService');
    }

    const orderedItems = getOrderedItems(orderState);

    if (orderedItems.length === 0) {
      return rejectWithValue('order.create.noOrderedItems');
    }

    const itemsPayload: CreateOrderItemPayload[] = [];

    for (const item of orderedItems) {
      const {
        id,
        uploadedFileId,
        quantity,
        paperTypeId,
        printerId,
        widthIn,
        heightIn,
        marginIn,
        galleryWrapIn,
        galleryWrapMirrorEdges,
        colorMode,
      } = item;

      const normalizedId = typeof id === 'string' ? id.trim() : '';
      if (normalizedId.length === 0) {
        logging.error('[order] Ordered item missing identifier', { item });
        return rejectWithValue('order.create.missingOrderItemId');
      }
      if (!uploadedFileId) {
        return rejectWithValue(`order.create.missingUploadedFile:${normalizedId}`);
      }
      if (!paperTypeId) {
        return rejectWithValue(`order.create.missingPaper:${normalizedId}`);
      }
      if (!printerId) {
        return rejectWithValue(`order.create.missingPrinter:${normalizedId}`);
      }
      if (typeof widthIn !== 'number' || !Number.isFinite(widthIn) || widthIn <= 0) {
        return rejectWithValue(`order.create.invalidWidth:${normalizedId}`);
      }
      if (typeof heightIn !== 'number' || !Number.isFinite(heightIn) || heightIn <= 0) {
        return rejectWithValue(`order.create.invalidHeight:${normalizedId}`);
      }

      itemsPayload.push({
        orderItemId: normalizedId,
        uploadedFileId,
        quantity: resolveQuantity(quantity),
        paperTypeId,
        printerId,
        widthIn,
        heightIn,
        marginIn: marginIn ?? null,
        galleryWrapIn: galleryWrapIn ?? null,
        galleryWrapMirrorEdges: galleryWrapMirrorEdges ?? false,
        colorMode: colorMode ?? null,
      });
    }

    const consumedItemIds = itemsPayload
      .map((entry) => entry.orderItemId)
      .filter((value): value is string => typeof value === 'string' && value.length > 0);

    try {
      const result = await createOrder({
        serviceId,
        items: itemsPayload,
        shippingAddress: buildCheckoutContactPayload(orderState.shippingAddress, orderState.fulfillmentMode),
        fulfillmentMode: orderState.fulfillmentMode,
      });

      const persistedId = hasPersistedOrderId(result.id) ? result.id.trim() : '';

      if (!persistedId) {
        return rejectWithValue('order.create.missingCreatedId');
      }

      return {
        raw: result,
        orderId: persistedId,
        orderItems: Array.isArray(result.orderItems)
          ? result.orderItems.map((entry) => ({
              id: entry.id,
              uploadedFileId: entry.uploadedFileId ?? undefined,
            }))
          : [],
        consumedItemIds,
      };
    } catch (error) {
      logging.error('[order] Failed to create order before payment', { error });
      const message = error instanceof Error ? error.message : 'order.create.unknownError';
      return rejectWithValue(message);
    }
  }
);

export const syncOrderForCheckout = createAsyncThunk<
  {
    orderId: string;
    raw: CreateOrderResult;
  },
  void,
  { state: RootState; rejectValue: string }
>(
  'order/syncOrderForCheckout',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const orderId = state.order.id;

    if (!hasPersistedOrderId(orderId)) {
      return rejectWithValue('order.update.missingOrderId');
    }

    const orderedItems = getOrderedItems(state.order)
      .filter((item) => typeof item?.id === 'string' && item.id.trim().length > 0);

    const itemsPayload: UpdateOrderPayload['items'] = orderedItems.map((item) => ({
      id: item.id,
      status: item.status ?? 'IN_ORDER',
      uploadedFileId: item.uploadedFileId ?? undefined,
      quantity: resolveQuantity(item.quantity),
      paperTypeId: item.paperTypeId ?? undefined,
      printerId: item.printerId ?? undefined,
      widthIn: typeof item.widthIn === 'number' ? item.widthIn : undefined,
      heightIn: typeof item.heightIn === 'number' ? item.heightIn : undefined,
      marginIn: item.marginIn ?? undefined,
      galleryWrapIn: item.galleryWrapIn ?? undefined,
      galleryWrapMirrorEdges: item.galleryWrapMirrorEdges ?? undefined,
      colorMode: item.colorMode ?? undefined,
    } satisfies UpdateOrderItemPayload));

    const payload: UpdateOrderPayload = {
      items: itemsPayload,
      shippingAddress: buildCheckoutContactPayload(state.order.shippingAddress, state.order.fulfillmentMode),
      fulfillmentMode: state.order.fulfillmentMode,
    };

    try {
      const result = await updateOrder(orderId, payload);
      return { orderId, raw: result };
    } catch (error) {
      logging.error('[order] Failed to sync existing order before payment', { error });
      const message = error instanceof Error ? error.message : 'order.update.unknownError';
      return rejectWithValue(message);
    }
  },
);

export const persistDirtyOrderItems = createAsyncThunk<void, { itemIds?: readonly string[] } | undefined, { state: RootState }>(
  'order/persistDirtyOrderItems',
  async (arg, { getState, dispatch, rejectWithValue }) => {
    const state = getState();
    const dirtyEntries = state.order.dirtyFieldsByItemId;
    if (!dirtyEntries || Object.keys(dirtyEntries).length === 0) {
      return;
    }

    const patches: OrderItemAutosavePatchPayload[] = [];
    const markSavedPayload: Array<{
      itemId: string;
      fields: OrderItemDirtyField[];
      snapshot: SavedFieldSnapshot;
    }> = [];

    const candidateIds = Array.isArray(arg?.itemIds) && arg.itemIds.length > 0
      ? arg.itemIds
      : Object.keys(dirtyEntries);

    for (const key of candidateIds) {
      const fieldsEntry = dirtyEntries[key];
      if (!fieldsEntry) {
        continue;
      }
      const relevantFields = (Object.keys(fieldsEntry) as OrderItemDirtyField[]).filter(
        (field) => fieldsEntry[field],
      );
      if (relevantFields.length === 0) {
        continue;
      }

      const target = state.order.items.find((candidate) => candidate.id === key);
      if (!target) {
        continue;
      }

      patches.push(buildPatchFromItem(target, relevantFields));

      const snapshot: SavedFieldSnapshot = {};
      for (const field of relevantFields) {
        setSavedFieldValue(snapshot, field, target[field]);
      }
      markSavedPayload.push({ itemId: target.id, fields: relevantFields, snapshot });
    }

    if (patches.length === 0) {
      return;
    }

    let response: ReadonlyArray<OrderItemAutosaveResponse>;
    try {
      response = await patchOrderItems(patches);
    } catch (error) {
      logging.error('[order] Failed to persist dirty order items', { error });
      const statusCode = (error as { response?: { status?: number } })?.response?.status;
      if (statusCode === 404) {
        try {
          const refreshed = await fetchPendingOrderItems();
          dispatch(setOrderState(buildOrderStateFromPendingSnapshot(refreshed, getState().order)));
          void dispatch(
            sendMessage(
              'Please let the user know that some saved order items were no longer available after the server restart, so their order list was refreshed from the latest server state.',
            ),
          );
        } catch (refreshError) {
          logging.error('[order] Failed to refresh pending order items after autosave 404', { error: refreshError });
        }
        return rejectWithValue(error);
      }
      void dispatch(
        sendMessage(
          'Please let the user know that we could not save their latest order changes due to a temporary server issue. Apologize and encourage them to retry in a moment.',
        ),
      );
      return rejectWithValue(error);
    }

    const responseById = new Map<string, OrderItemAutosaveResponse>();
    for (const entry of response) {
      responseById.set(entry.id, entry);
      const priceValue = typeof entry.priceUsd === 'number' && Number.isFinite(entry.priceUsd)
        ? entry.priceUsd
        : null;
      if (priceValue != null) {
        dispatch(updateOrderItem({
          orderItemId: entry.id,
          data: {
            price: { value: priceValue, currency: 'USD' },
            pricingStatus: 'succeeded',
            pricingError: null,
          },
        }));
      }
    }

    const reconciledPayload = markSavedPayload.map((entry) => {
      const remote = responseById.get(entry.itemId);
      if (!remote) {
        return entry;
      }
      const snapshot: SavedFieldSnapshot = { ...entry.snapshot };
      for (const field of entry.fields) {
        switch (field) {
          case 'quantity': {
            setSavedFieldValue(snapshot, 'quantity', resolveQuantity(remote.quantity));
            break;
          }
          case 'paperTypeId': {
            const value = remote.paperTypeId;
            const next = typeof value === 'string' && value.length > 0 ? value : undefined;
            setSavedFieldValue(snapshot, 'paperTypeId', next);
            break;
          }
          case 'printerId': {
            const value = remote.printerId;
            const next = typeof value === 'string' && value.length > 0 ? value : undefined;
            setSavedFieldValue(snapshot, 'printerId', next);
            break;
          }
          case 'colorMode': {
            const value = remote.colorMode;
            const next = value === 'COLOR' || value === 'BLACK_WHITE' ? value : undefined;
            setSavedFieldValue(snapshot, 'colorMode', next);
            break;
          }
          case 'widthIn': {
            const value = remote.widthIn;
            const next = typeof value === 'number' && Number.isFinite(value) ? value : undefined;
            setSavedFieldValue(snapshot, 'widthIn', next);
            break;
          }
          case 'heightIn': {
            const value = remote.heightIn;
            const next = typeof value === 'number' && Number.isFinite(value) ? value : undefined;
            setSavedFieldValue(snapshot, 'heightIn', next);
            break;
          }
          case 'marginIn': {
            const value = remote.marginIn;
            const next = typeof value === 'number' && Number.isFinite(value) ? value : undefined;
            setSavedFieldValue(snapshot, 'marginIn', next);
            break;
          }
          case 'galleryWrapIn': {
            const value = remote.galleryWrapIn;
            const next = typeof value === 'number' && Number.isFinite(value) ? value : undefined;
            setSavedFieldValue(snapshot, 'galleryWrapIn', next);
            break;
          }
          case 'galleryWrapMirrorEdges': {
            const value = remote.galleryWrapMirrorEdges;
            const next = typeof value === 'boolean' ? value : undefined;
            setSavedFieldValue(snapshot, 'galleryWrapMirrorEdges', next);
            break;
          }
          case 'printQualityWarning': {
            setSavedFieldValue(snapshot, 'printQualityWarning', remote.printQualityWarning ?? undefined);
            break;
          }
        }
      }
      return { ...entry, snapshot };
    });

    dispatch(markOrderItemsSaved(reconciledPayload));
  },
);

export const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    // Ensure we never store duplicate service information under `order`
    // by stripping a potential `currentService` field coming from AI payloads
    setShippingAddress: (state, action: PayloadAction<ShippingAddress>) => {
      const nextAddress = action.payload;
      const addressChanged = hasShippingAddressChanged(state.shippingAddress, nextAddress);
      state.shippingAddress = nextAddress;
      if (addressChanged) {
        resetShippingQuoteState(state);
      }
    },
    setFulfillmentMode: (state, action: PayloadAction<OrderFulfillmentMode>) => {
      const nextMode = action.payload;
      if (state.fulfillmentMode === nextMode) {
        return;
      }
      state.fulfillmentMode = nextMode;
      state.paymentInformation = applyFulfillmentModeToPaymentInformation(state.paymentInformation, nextMode);
      resetShippingQuoteState(state);
    },
    addOrderItem: (
      state,
      action: PayloadAction<{
        orderItemId: string;
        originalWidthPx?: number;
        originalHeightPx?: number;
        originalWidthIn?: number;
        originalHeightIn?: number;
        dpiX?: number;
        dpiY?: number;
      }>
    ) => {
      const {
        orderItemId,
        originalWidthPx,
        originalHeightPx,
        originalWidthIn,
        originalHeightIn,
        dpiX,
        dpiY,
      } = action.payload;
      const existingItem = state.items.find(item => item.id === orderItemId);
      if (!existingItem) {
        ensureTrackingMaps(state);
        state.items.push({
          id: orderItemId,
          status: 'NEW',
          quantity: 1,
          originalWidthPx,
          originalHeightPx,
          originalWidthIn,
          originalHeightIn,
          dpiX,
          dpiY,
          colorMode: 'COLOR',
          pricingStatus: 'idle',
          pricingError: null,
        });
        const inserted = state.items[state.items.length - 1];
        state.lastSavedFieldValuesByItemId[inserted.id] = state.lastSavedFieldValuesByItemId[inserted.id] ?? pickTrackedFieldValues(inserted);
        syncDirtyStateForItem(state, inserted.id);
        resetShippingQuoteState(state);
      }
    },
    updateOrderItem: (state, action: PayloadAction<{ orderItemId: string; data: Partial<OrderItem> }>) => {
      const { orderItemId, data } = action.payload;
      ensureTrackingMaps(state);
      const itemIndex = state.items.findIndex(item => item.id === orderItemId);
      if (itemIndex === -1) {
        const created: OrderItem = {
          id: orderItemId,
          status: 'NEW',
          quantity: 1,
          pricingStatus: 'loading',
          pricingError: null,
        };

        const merged = {
          ...created,
          ...data,
          status: data.status ?? created.status,
        } satisfies OrderItem;

        if ('price' in data) {
          if (data.price) {
            merged.pricingStatus = 'succeeded';
            merged.pricingError = null;
          } else {
            merged.price = undefined;
            merged.pricingStatus = 'loading';
            merged.pricingError = null;
          }
        }

        state.items.push(merged);
        state.lastSavedFieldValuesByItemId[orderItemId] = state.lastSavedFieldValuesByItemId[orderItemId] ?? {};
        syncDirtyStateForItem(state, orderItemId);
        resetShippingQuoteState(state);
        return;
      }
      if (itemIndex !== -1) {
        const targetId = state.items[itemIndex].id;
        const previous = state.items[itemIndex];
        const nextStatus = data.status ?? previous.status ?? 'NEW';
        state.items[itemIndex] = {
          ...previous,
          ...data,
          status: nextStatus,
        };

        const item = state.items[itemIndex];
        if ('price' in data) {
          if (data.price) {
            item.pricingStatus = 'succeeded';
            item.pricingError = null;
          } else {
            item.price = undefined;
            item.pricingStatus = 'loading';
            item.pricingError = null;
          }
        }

        state.lastSavedFieldValuesByItemId[targetId] = state.lastSavedFieldValuesByItemId[targetId] ?? {};
        syncDirtyStateForItem(state, targetId);
        if (hasPackagingRelevantOrderItemChanges(previous, item)) {
          resetShippingQuoteState(state);
        }
      }
    },
    removeOrderItemById: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      ensureTrackingMaps(state);
      const target = state.items.find(item => item.id === itemId);
      if (target) {
        delete state.dirtyFieldsByItemId[target.id];
        delete state.lastSavedFieldValuesByItemId[target.id];
      }
      state.items = state.items.filter(item => item.id !== itemId);
      state.selectedItemIds = normalizeSelection(state.items, state.selectedItemIds);
      resetShippingQuoteState(state);
    },
    restoreOrderItem: (state, action: PayloadAction<{ item: OrderItem; index?: number }>) => {
      const { item, index } = action.payload;
      const existingWithoutTarget = state.items.filter(existing => existing.id !== item.id);
      if (typeof index === 'number' && index >= 0 && index <= existingWithoutTarget.length) {
        existingWithoutTarget.splice(index, 0, item);
      } else {
        existingWithoutTarget.unshift(item);
      }
      state.items = existingWithoutTarget;
      state.selectedItemIds = normalizeSelection(state.items, state.selectedItemIds);
      ensureTrackingMaps(state);
      state.lastSavedFieldValuesByItemId[item.id] = pickTrackedFieldValues(item);
      delete state.dirtyFieldsByItemId[item.id];
      resetShippingQuoteState(state);
    },
    clearOrder: () => {
      return initialState;
    },
    setItemSelection: (state, action: PayloadAction<{ itemId: string; selected: boolean }>) => {
      const { itemId, selected } = action.payload;
      if (!itemId) {
        return;
      }
      const current = new Set(state.selectedItemIds);
      if (selected) {
        current.add(itemId);
      } else {
        current.delete(itemId);
      }
      state.selectedItemIds = clampSelection(Array.from(current));
    },
    clearSelectedItems: (state) => {
      state.selectedItemIds = [];
    },
    updateShippingAddress: (state, action: PayloadAction<Partial<ShippingAddress>>) => {
      if (state.shippingAddress) {
        state.shippingAddress = {
          ...state.shippingAddress,
          ...action.payload,
        };
      } else {
        // Handle case where shippingAddress might be undefined
        state.shippingAddress = action.payload as ShippingAddress;
      }
    },
    resetShippingQuote: (state) => {
      resetShippingQuoteState(state);
    },
    invalidatePricingCache: (state) => {
      state.pricingSummary = undefined;
      state.pricingCacheVersion = (state.pricingCacheVersion ?? 0) + 1;

      for (const item of state.items) {
        const normalizedStatus = typeof item.status === 'string'
          ? item.status.trim().toUpperCase()
          : '';

        if (normalizedStatus && isOrderedStatus(normalizedStatus)) {
          continue;
        }

        item.price = undefined;
        item.pricingStatus = 'idle';
        item.pricingError = null;
      }
    },
    completeOrderFulfillment: (state) => {
      const currentOrderId = state.id;

      const retainedItems = state.items
        .filter((item) => {
          const normalizedStatus = typeof item.status === 'string'
            ? item.status.trim().toUpperCase()
            : '';

          if (isOrderedStatus(normalizedStatus)) {
            return false;
          }

          if (!currentOrderId) {
            return true;
          }

          const belongsToCurrentOrder = Boolean(
            typeof item.orderId === 'string'
            && item.orderId.length > 0
            && item.orderId === currentOrderId,
          );

          return !belongsToCurrentOrder;
        })
        .map((item) => ({ ...item } satisfies OrderItem));

      if (retainedItems.length === 0) {
        return {
          ...initialState,
          items: [],
          dirtyFieldsByItemId: {},
          lastSavedFieldValuesByItemId: {},
        } satisfies OrderState;
      }

      const snapshots: Record<string, SavedFieldSnapshot> = {};
      for (const item of retainedItems) {
        snapshots[item.id] = pickTrackedFieldValues(item);
      }

      return {
        ...initialState,
        items: retainedItems,
        id: undefined,
        status: undefined,
        dirtyFieldsByItemId: {},
        lastSavedFieldValuesByItemId: snapshots,
      } satisfies OrderState;
    },
    setOrderState: (state, action: PayloadAction<OrderState | undefined>) => {
      if (!action.payload) {
        return state;
      }

      const { currentService: _ignoredService, ...clean } = (action.payload as unknown as OrderState & {
        currentService?: unknown;
      });

      const itemsFromPayload = Array.isArray(clean.items) ? clean.items : null;
      const existingById = new Map(state.items.map((item) => [item.id, item] as const));

      const mergedItems = itemsFromPayload
        ? itemsFromPayload.map((incoming) => {
            if (!incoming || typeof incoming.id !== 'string') {
              return incoming;
            }
            const existing = existingById.get(incoming.id);
            if (!existing) {
              return incoming;
            }

            const normalizedIncomingStatus =
              typeof incoming.status === 'string' ? incoming.status.trim().toUpperCase() : undefined;
            const normalizedExistingStatus =
              typeof existing.status === 'string' ? existing.status.trim().toUpperCase() : undefined;

            let resolvedStatus: OrderItem['status'] | undefined;
            if (!normalizedIncomingStatus) {
              resolvedStatus = existing.status;
            } else if (
              normalizedIncomingStatus === 'NEW'
              && normalizedExistingStatus
              && isOrderedStatus(normalizedExistingStatus)
            ) {
              resolvedStatus = normalizedExistingStatus as OrderItem['status'];
            } else {
              resolvedStatus = normalizedIncomingStatus as OrderItem['status'];
            }

            return {
              ...existing,
              ...incoming,
              status: resolvedStatus,
              pricingStatus: incoming.pricingStatus ?? existing.pricingStatus,
              pricingError: incoming.pricingError ?? existing.pricingError,
              orderId: incoming.orderId !== undefined ? incoming.orderId : existing.orderId,
            } satisfies OrderItem;
          })
        : null;

      const base: OrderState = {
        ...state,
        ...clean,
        items: mergedItems ?? clean.items ?? state.items,
        dirtyFieldsByItemId: state.dirtyFieldsByItemId,
        lastSavedFieldValuesByItemId: state.lastSavedFieldValuesByItemId,
      };

      ensureTrackingMaps(base);

      const normalized = normalizeToItemCentric(base);

      if (Array.isArray(clean.items)) {
        resetDirtyStateUsingCurrentItems(normalized);
      }

      Object.assign(state, normalized);
    },
    markOrderItemsSaved: (
      state,
      action: PayloadAction<
        {
          itemId: string;
          fields?: OrderItemDirtyField[];
          snapshot?: Partial<OrderItem>;
        }[]
      >,
    ) => {
      ensureTrackingMaps(state);
      for (const entry of action.payload) {
        if (!entry?.itemId) {
          continue;
        }
        const target = findItemById(state, entry.itemId);
        const resolvedId = entry.itemId;
        if (!resolvedId) {
          continue;
        }

        const notes = entry.snapshot ?? target ?? undefined;
        const fieldsToUpdate = entry.fields ?? ORDER_ITEM_AUTOSAVE_FIELDS;
        const nextSnapshot: SavedFieldSnapshot = {
          ...(state.lastSavedFieldValuesByItemId[resolvedId] ?? {}),
        };

        const sourceValues = notes as Partial<OrderItem> | undefined;
        for (const field of fieldsToUpdate) {
          const hasField = sourceValues && Object.prototype.hasOwnProperty.call(sourceValues, field);
          const currentValue = hasField ? sourceValues?.[field] : undefined;
          setSavedFieldValue(nextSnapshot, field, currentValue);
        }

        state.lastSavedFieldValuesByItemId[resolvedId] = nextSnapshot;
        syncDirtyStateForItem(state, resolvedId);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createOrderForCheckout.pending, (state) => {
        state.checkoutPersistenceInFlight = true;
      })
      .addCase(createOrderForCheckout.fulfilled, (state, action) => {
        if (hasPersistedOrderId(state.id) && state.id !== action.payload.orderId) {
          logging.info('[order] Ignoring stale createOrderForCheckout result for a different active order', {
            activeOrderId: state.id,
            incomingOrderId: action.payload.orderId,
          });
          state.checkoutPersistenceInFlight = false;
          return;
        }
        state.checkoutPersistenceInFlight = false;
        state.id = action.payload.orderId;
        const consumed = action.payload.consumedItemIds;
        if (Array.isArray(consumed) && consumed.length > 0) {
          removeItemsById(state, consumed);
        }
        hydrateOrderStateFromResult(state, action.payload.raw);
      })
      .addCase(createOrderForCheckout.rejected, (state) => {
        state.checkoutPersistenceInFlight = false;
      })
      .addCase(syncOrderForCheckout.pending, (state) => {
        state.checkoutPersistenceInFlight = true;
      })
      .addCase(syncOrderForCheckout.fulfilled, (state, action) => {
        if (hasPersistedOrderId(state.id) && state.id !== action.payload.orderId) {
          logging.info('[order] Ignoring stale syncOrderForCheckout result for a different active order', {
            activeOrderId: state.id,
            incomingOrderId: action.payload.orderId,
          });
          state.checkoutPersistenceInFlight = false;
          return;
        }
        state.checkoutPersistenceInFlight = false;
        state.id = action.payload.orderId;
        hydrateOrderStateFromResult(state, action.payload.raw);
      })
      .addCase(syncOrderForCheckout.rejected, (state) => {
        state.checkoutPersistenceInFlight = false;
      })
      .addCase(recalculateOrderItemPrice.pending, (state, action) => {
        const { orderItemId } = action.meta.arg;
        const item = state.items.find((candidate) => candidate.id === orderItemId);
        if (item) {
          item.pricingStatus = 'loading';
          item.pricingError = null;
        }
      })
      .addCase(recalculateOrderItemPrice.fulfilled, (state, action) => {
        const { orderItemId, price, resolvedPaperTypeId, resolvedPrinterId, dimensions } = action.payload;
        const item = state.items.find((candidate) => candidate.id === orderItemId);
        if (item) {
          const previousWidthIn = item.widthIn;
          const previousHeightIn = item.heightIn;
          item.price = price;
          if (
            typeof item.paperTypeId === 'string'
            && item.paperTypeId.length > 0
            && typeof resolvedPaperTypeId === 'string'
            && resolvedPaperTypeId.length > 0
          ) {
            item.paperTypeId = resolvedPaperTypeId;
          }
          if (
            typeof item.printerId === 'string'
            && item.printerId.length > 0
            && typeof resolvedPrinterId === 'string'
            && resolvedPrinterId.length > 0
          ) {
            item.printerId = resolvedPrinterId;
          }
          if (typeof dimensions.widthInches === 'number') {
            item.widthIn = dimensions.widthInches;
          }
          if (typeof dimensions.heightInches === 'number') {
            item.heightIn = dimensions.heightInches;
          }
          item.pricingStatus = 'succeeded';
          item.pricingError = null;

          if (
            !areOptionalNumbersEqual(previousWidthIn, item.widthIn)
            || !areOptionalNumbersEqual(previousHeightIn, item.heightIn)
          ) {
            resetShippingQuoteState(state);
          }
        }
      })
      .addCase(recalculateOrderItemPrice.rejected, (state, action) => {
        const { orderItemId } = action.meta.arg;
        const item = state.items.find((candidate) => candidate.id === orderItemId);
        if (item) {
          item.pricingStatus = 'failed';
          item.pricingError = (typeof action.payload === 'string' ? action.payload : action.error.message) ?? 'Unable to calculate price';
        }
      })
      .addCase(fetchShippingQuotes.pending, (state) => {
        state.shippingQuoteStatus = 'loading';
        state.shippingQuoteError = null;
      })
      .addCase(fetchShippingQuotes.fulfilled, (state, action) => {
        state.shippingQuoteStatus = 'succeeded';
        state.shippingQuote = {
          ...action.payload.quote,
          options: action.payload.quote.options.map((option) => ({
            ...option,
            warnings: option.warnings ? [...option.warnings] : undefined,
          })),
        };
        state.packagingSummary = action.payload.packaging;
        state.shippingQuoteError = null;
      })
      .addCase(fetchShippingQuotes.rejected, (state, action) => {
        state.shippingQuoteStatus = 'failed';
        state.shippingQuote = undefined;
        state.packagingSummary = undefined;
        state.shippingQuoteError = (typeof action.payload === 'string' ? action.payload : action.error.message) ?? 'order.shipping.quoteFailed';
      });

    builder.addCase(sendMessage.fulfilled, (state, action) => {
      const payload = action.payload as any;
      // If the UI just confirmed an order, ensure we do NOT rehydrate state from payload
      const uiName = payload?.uiPayload?.name as string | undefined;
      if (uiName === 'orderConfirmation') {
        return initialState;
      }
      const stateFromUi = payload?.uiPayload?.updatedState;
      const stateFromTop = payload?.updatedState;
      const incomingRaw = stateFromUi || stateFromTop;
      const { currentService: _ignoredService, ...incoming } = (incomingRaw as unknown as OrderState & {
        currentService?: unknown;
      }) || ({} as OrderState);
      if (incoming && Object.keys(incoming).length > 0) {
        const base = {
          ...state,
          ...incoming,
          items: incoming.items ?? state.items,
          dirtyFieldsByItemId: state.dirtyFieldsByItemId,
          lastSavedFieldValuesByItemId: state.lastSavedFieldValuesByItemId,
        } as OrderState;
        ensureTrackingMaps(base);
        const normalized = normalizeToItemCentric(base);
        if (Array.isArray(incoming.items)) {
          resetDirtyStateUsingCurrentItems(normalized);
        }
        return normalized;
      }
      return state;
    });
  },
});

export const {
  addOrderItem,
  updateOrderItem,
  removeOrderItemById,
  restoreOrderItem,
  clearOrder,
  setOrderState,
  setItemSelection,
  clearSelectedItems,
  markOrderItemsSaved,
  completeOrderFulfillment,
  resetShippingQuote,
  invalidatePricingCache,
} = orderSlice.actions;
export const { setShippingAddress, setFulfillmentMode, updateShippingAddress } = orderSlice.actions;

export const selectOrderItems = (state: RootState) => state.order.items;
export const selectOrderState = (state: RootState) => state.order;
export const selectShippingAddress = (state: RootState) => state.order.shippingAddress;
export const selectSelectedItemIds = (state: RootState) => state.order.selectedItemIds;
export const selectDirtyFieldsByItemId = (state: RootState) => state.order.dirtyFieldsByItemId;
export const selectLastSavedSnapshotsByItemId = (state: RootState) => state.order.lastSavedFieldValuesByItemId;
export const selectShippingQuote = (state: RootState) => state.order.shippingQuote;
export const selectShippingQuoteStatus = (state: RootState) => state.order.shippingQuoteStatus ?? 'idle';
export const selectShippingQuoteError = (state: RootState) => state.order.shippingQuoteError;
export const selectPackagingSummary = (state: RootState) => state.order.packagingSummary;

// Determine if an order contains meaningful information beyond empty placeholders
const hasMeaningfulItem = (item: OrderItem | Record<string, unknown> | undefined | null): boolean => {
  if (!item || typeof item !== 'object') return false;
  // Consider an item meaningful only if it has a persisted identifier
  return Boolean((item as OrderItem).id);
};

export const selectHasMeaningfulOrder = (state: RootState): boolean => {
  const order = selectOrderState(state);
  const itemsHaveMeaning = Array.isArray(order.items) && order.items.some(hasMeaningfulItem);
  const hasShipping = !!order.shippingAddress;
  return itemsHaveMeaning || hasShipping;
};

// Only considers items; ignores shipping address.
export const selectOrderPricingCacheVersion = (state: RootState): number => state.order.pricingCacheVersion ?? 0;

export const selectHasMeaningfulItems = (state: RootState): boolean => {
  const order = selectOrderState(state);
  return Array.isArray(order.items) && order.items.some(hasMeaningfulItem);
};

export default orderSlice.reducer;
